import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Moon, Sun, Languages, Building2, Eye, EyeOff } from "lucide-react";
import { AUTH } from "@/constants/testIds";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const { t, toggle: toggleLang, lang } = useI18n();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== null && user !== false) {
    return <Navigate to="/" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/", { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      {/* Left / brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r border-border/60 overflow-hidden grain">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-foreground text-background grid place-items-center font-bold">P</div>
            <div>
              <div className="font-heading font-semibold tracking-tight">{t("app.name")}</div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{t("app.tagline")}</div>
            </div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="font-heading text-5xl xl:text-6xl leading-[1.05] tracking-tight font-semibold">
            El portfolio<br/>de tu familia,<br/>
            <span className="text-muted-foreground">en una vista.</span>
          </h1>
          <p className="mt-5 max-w-md text-sm text-muted-foreground leading-relaxed">
            Argentina, Estados Unidos y México — todos los ingresos, gastos y rentabilidades convertidos a USD, con roles y alertas.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-4">
            {[
              { c: "USD", label: "EE.UU.", color: "text-emerald-500", dot: "bg-emerald-400" },
              { c: "ARS", label: "Argentina", color: "text-sky-500", dot: "bg-sky-400" },
              { c: "MXN", label: "México", color: "text-amber-500", dot: "bg-amber-400" },
            ].map((x) => (
              <div key={x.c} className="rounded-lg border border-border/60 bg-card/60 p-3">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${x.dot}`} /> {x.label}
                </div>
                <div className={`mt-1 font-heading text-xl font-semibold ${x.color}`}>{x.c}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-muted-foreground">© {new Date().getFullYear()} Portfolio Familiar</div>
      </div>

      {/* Right / form */}
      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <div className="absolute top-4 right-4 flex gap-2">
          <Button variant="outline" size="icon" onClick={toggleTheme} data-testid={AUTH.themeToggle}>
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button variant="outline" size="sm" onClick={toggleLang} data-testid={AUTH.languageToggle}>
            <Languages className="w-4 h-4 mr-1" /> {lang.toUpperCase()}
          </Button>
        </div>
        <Card className="w-full max-w-md border-border/60 shadow-none">
          <CardHeader className="space-y-1">
            <div className="lg:hidden mb-2 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              <span className="font-heading font-semibold">{t("app.name")}</span>
            </div>
            <CardTitle className="font-heading text-3xl">{t("login.title")}</CardTitle>
            <CardDescription>{t("login.subtitle")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t("login.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid={AUTH.loginEmail}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t("login.password")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid={AUTH.loginPassword}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-secondary/60 transition-colors"
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    tabIndex={-1}
                    data-testid="login-password-toggle"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {error && (
                <div
                  data-testid={AUTH.loginError}
                  className="text-sm text-rose-500 bg-rose-500/10 border border-rose-500/20 rounded-md px-3 py-2"
                >
                  {error}
                </div>
              )}
              <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid={AUTH.loginSubmit}>
                {loading ? "…" : t("login.submit")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
